import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppBar, Box, IconButton, Typography, Avatar, MenuItem, Menu, Modal, Button, ListItemIcon, Grid, CircularProgress } from '@mui/material';
import { KeyboardArrowDown, Logout } from '@mui/icons-material';
// import { useSelector } from 'react-redux';
import { signOut } from 'firebase/auth';
import { CommHandler } from '../utils';
import { auth } from '../firebase';
import { Image } from '../components';
import YearDropdown from '../utils/YearDropdown';

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirmLogout: () => void | Promise<void>;
  loading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({ open, onClose, onConfirmLogout, loading = false }) => (
  <Modal open={open} onClose={onClose} aria-labelledby="logout-confirmation-modal" aria-describedby="logout-confirmation-modal">
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 296,
        bgcolor: 'background.paper',
        borderRadius: 3,
        p: 4,
      }}
    >
      <Typography variant="h6" id="logout-confirmation-modal" component="div" align="center" fontWeight="600" color="#000000">
        Are you sure you want to logout?
      </Typography>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 2,
          gap: '8px',
        }}
      >
        <Button
          variant="outlined"
          sx={{
            mr: 1,
            backgroundColor: 'white',
            textTransform: 'none',
            color: '#2559C3',
            borderColor: '#2559C3',
          }}
          onClick={onClose}
          disabled={loading}
        >
          Go back
        </Button>
        <Button
          variant="contained"
          sx={{
            mr: 1,
            backgroundColor: '#2559C3',
            textTransform: 'none',
            color: '#fff',
          }}
          onClick={onConfirmLogout}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Logout'}
        </Button>
      </Box>
    </Box>
  </Modal>
);

interface Props {
  onYearChange?: (year: string) => void;
  currentFinancialYear?: string;
}

const Header: React.FC<Props> = ({ onYearChange = () => {}, currentFinancialYear = '' }) => {
  // const user = useSelector((state: any) => state.user.employee);
  // const { firstName, profile } = user;
  const location = useLocation();
  const isAppraisalOrReports = location.pathname === '/appraisal-allocation' || location.pathname === '/reports';
  const navigate = useNavigate();
  const fullName = localStorage.getItem('firstName');
  const depart = localStorage.getItem('department');
  const imagePath = localStorage.getItem('image');
  const profile = { department: depart, designation: 'Software Engineer' };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    setShowConfirmation(true);
  };

  const handleConfirmLogout = async () => {
    try {
      setLoading(true);
      // Best-effort attempt to notify backend of logout
      // CommHandler automatically adds auth headers via interceptor
      await CommHandler.get('/logout');
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with logout even if API call fails
    } finally {
      // Always clear client auth state regardless of API success
      localStorage.clear();
      await signOut(auth);
      setShowConfirmation(false);
      setLoading(false);
      navigate('/');
    }
  };

  const handleModalClose = () => {
    setShowConfirmation(false);
  };
  return (
    <AppBar
      position="fixed"
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: '#fff',
        padding: '10px',
        boxShadow: 'none',
        borderBottom: '1px solid #EEEEEE',
      }}
    >
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Image src="image 5.png" />
        </Grid>

        <Grid container item xs={6} justifyContent="flex-end" alignItems="center" spacing={2}>
          <Grid item>{isAppraisalOrReports && <YearDropdown onYearChange={onYearChange} initialValue={currentFinancialYear} />}</Grid>
          <Grid item>
            <Grid container alignItems="center" spacing={1}>
              <Grid item>
                <Avatar alt="Profile Picture" src={imagePath || undefined} />
              </Grid>
              <Grid item>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: '14px',
                    lineHeight: '19px',
                    color: '#000000',
                    fontFamily: 'Lato',
                  }}
                >
                  {fullName}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: '500',
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#2559C3',
                  }}
                >
                  {profile?.department}
                </Typography>
              </Grid>
              <Grid item>
                <IconButton size="large" edge="end" aria-label="account of current user" aria-haspopup="true" color="inherit" onClick={handleClick}>
                  <KeyboardArrowDown style={{ color: '#2559C3' }} />
                </IconButton>
                <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <Logout fontSize="small" />
                    </ListItemIcon>
                    Logout
                  </MenuItem>
                </Menu>
                <ConfirmationModal open={showConfirmation} onClose={handleModalClose} onConfirmLogout={handleConfirmLogout} loading={loading} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AppBar>
  );
};

Header.defaultProps = {
  onYearChange: () => {},
  currentFinancialYear: '',
};

export default Header;
